import { useCallback, useEffect, useReducer } from 'react';

/**
 * Slot+slot selection inside one day of the timeline.
 *
 * Positions are slots *within* the day (`0 … 47`); the absolute range is formed
 * by the caller, which knows the day. A tap includes the tapped slot, so the
 * range end is one slot past it.
 */
export type SlotSelection =
  | { mode: 'idle' }
  | { mode: 'anchor'; slotInDay: number }
  | { mode: 'range'; fromSlotInDay: number; toSlotInDay: number };

type SelectionAction = { type: 'tap'; slotInDay: number } | { type: 'reset' };

const IDLE: SlotSelection = { mode: 'idle' };

function reduce(state: SlotSelection, action: SelectionAction): SlotSelection {
  if (action.type === 'reset') return IDLE;

  switch (state.mode) {
    case 'idle':
      return { mode: 'anchor', slotInDay: action.slotInDay };
    case 'anchor':
      if (state.slotInDay === action.slotInDay) return IDLE;
      return {
        mode: 'range',
        fromSlotInDay: Math.min(state.slotInDay, action.slotInDay),
        toSlotInDay: Math.max(state.slotInDay, action.slotInDay) + 1,
      };
    case 'range':
      return { mode: 'anchor', slotInDay: action.slotInDay };
  }
}

export interface SlotSelectionApi {
  selection: SlotSelection;
  tap: (slotInDay: number) => void;
  reset: () => void;
}

export function useSlotSelection(dayIndex: number): SlotSelectionApi {
  const [selection, dispatch] = useReducer(reduce, IDLE);

  const tap = useCallback((slotInDay: number) => dispatch({ type: 'tap', slotInDay }), []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [dayIndex]);

  useEffect(() => {
    if (selection.mode === 'idle') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dispatch({ type: 'reset' });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selection.mode]);

  return { selection, tap, reset };
}

/**
 * Slots the dashed overlay covers. A lone anchor already gets the outline —
 * without it the first tap would look like it did nothing.
 */
export function selectedSlots(
  selection: SlotSelection,
): { fromSlotInDay: number; toSlotInDay: number } | null {
  switch (selection.mode) {
    case 'idle':
      return null;
    case 'anchor':
      return { fromSlotInDay: selection.slotInDay, toSlotInDay: selection.slotInDay + 1 };
    case 'range':
      return { fromSlotInDay: selection.fromSlotInDay, toSlotInDay: selection.toSlotInDay };
  }
}
